// Guard rail against loop-calling an API that keeps throwing.
//
// A failing endpoint drives three loop shapes across the apps: a self-scheduling
// poll (setInterval / recursive setTimeout), a socket that reconnects on close,
// and a React effect that re-fires because its error path never records a
// terminal state. All three reduce to the same need — count consecutive
// failures and stop once a cap is hit. Reset the counter on any success so a
// transient blip never accumulates toward the cap.
//
// This is intentionally framework-agnostic and time-agnostic: callers already
// own their own spacing (poll interval, socket backoff, effect deps), so the
// guard only decides *whether* another attempt is allowed, never *when*.
export class RetryGuard {
    private failures = 0

    // ponytail: plain counter, no backoff clock — callers space their own retries.
    constructor(private readonly maxFailures = 5) {
        if (maxFailures < 1) throw new Error('RetryGuard maxFailures must be >= 1')
    }

    /** True once consecutive failures have hit the cap — gate the next call on `!exhausted`. */
    get exhausted(): boolean {
        return this.failures >= this.maxFailures
    }

    get failureCount(): number {
        return this.failures
    }

    /** Record a failed attempt. */
    fail(): void {
        this.failures += 1
    }

    /** Record a successful attempt — clears the failure streak. */
    succeed(): void {
        this.failures = 0
    }

    /** Clear the counter without a success (e.g. a manual user-triggered retry). */
    reset(): void {
        this.failures = 0
    }
}
